import * as React from 'react';
import { useState } from 'react';
import { toast } from 'react-toastify';
import { Tooltip } from 'react-tooltip';

import { AdhocDiscountModal } from './AdhocDiscountModal';
import { ConfirmationModal } from './ConfirmationModal';
import { DiscountDeletionModal } from './DiscountDeletionModal';
import { MarkCompleteModal, TimestampsMap } from './MarkCompleteModal';
import { PostCancelModal } from './PostCancelModal';
import { Button } from './buttons/Button';
import { axios } from '../api';
import { TrashIcon } from '../assets/TrashIcon';
import { isAdmin, isOpsAdmin, isAllModuleAccessUser, isTeamMember } from '../pages/constants';
import { Customer, OrderStatus } from '../pages/types';
import {
  BOTTOM_TOAST,
  generateErrorMsg,
  getModificationOrderEligibility,
  getRefundEligibility,
  mapSntApiDate,
  parseNumber,
} from '../utils';

interface OrderDetailProps {
  customer: Customer;
  order: any;
  refreshCustomer: () => void;
  disputeStatus: string | null;
  isLoadingDisputeStatus?: boolean;
}

const TWO_MINUTE_LONG_TOAST = 120000; // 2 minutes in milliseconds

export const OrderDetail = ({
  customer,
  order,
  refreshCustomer,
  disputeStatus,
  isLoadingDisputeStatus,
}: OrderDetailProps) => {
  const [showCancelConfirmModal, setShowCancelConfirmModal] = useState(false);
  const [showRefundConfirmModal, setShowRefundConfirmModal] = useState(false);
  const [showPostCancelModal, setShowPostCancelModal] = useState(false);
  const [showAdhocDiscountModal, setShowAdhocDiscountModal] = useState(false);
  const [showDeleteDiscountModal, setShowDeleteDiscountModal] = useState(false);
  const [isCancellingOrder, setIsCancellingOrder] = useState(false);
  const [showMarkCompleteModal, setShowMarkCompleteModal] = useState(false);
  const [isMarkingComplete, setIsMarkingComplete] = useState(false);
  const showDiscountActions = !!(
    isAdmin() ||
    isOpsAdmin() ||
    isAllModuleAccessUser() ||
    isTeamMember()
  );

  if (!order) {
    return <></>;
  }

  const orderDate = order.scheduled ? order.scheduled : order.charged;

  let petNamesStr = '';
  const petNames: string[] = [];
  if (order.products.length > 0) {
    order.products.forEach((item: any) => {
      // TODO: pet_plan is null when we add one-time product to order, need to fix it.
      const petName = item.pet_plan?.pet_name;
      if (petName && !petNames.includes(petName)) {
        petNames.push(petName);
      }
    });

    // Create a deduplicated string of pet names
    petNamesStr = petNames.join(', ');
  }

  const orderContainsGoBoltFulfillment =
    order.fulfillments?.some((f: any) => f.fulfillment_center === 'GB-CGY') ?? false;

  const canMarkComplete = isAdmin() || isOpsAdmin() || isAllModuleAccessUser();
  const hasRefundableBalance =
    parseNumber(order.total_price) - parseNumber(order.total_refunds) > 0;
  const isEligibleForModificationOrder = getModificationOrderEligibility(order);
  const isRefundEligible = getRefundEligibility(order.charge, order, disputeStatus);

  const changeOrderStatusToCancelled = () => {
    setIsCancellingOrder(true);
    axios
      .put(`orders/mark_order_cancelled`, {
        order_id: order.id,
      })
      .then(() => {
        order.status = 'CANCELLED';
        setShowCancelConfirmModal(false);
        toast.success('Order status was marked as cancelled.', {
          ...BOTTOM_TOAST,
          autoClose: TWO_MINUTE_LONG_TOAST,
        });
        // Should reflect latest status from above
        if (isAllModuleAccessUser()) {
          if (
            getModificationOrderEligibility(order) ||
            (isRefundEligible && hasRefundableBalance)
          ) {
            setShowPostCancelModal(true);
          }
        } else {
          if (hasRefundableBalance) {
            setShowRefundConfirmModal(true);
          }
        }
      })
      .catch((e) => {
        setShowCancelConfirmModal(false);
        toast.error(generateErrorMsg(e), {
          ...BOTTOM_TOAST,
          autoClose: TWO_MINUTE_LONG_TOAST,
        });
      })
      .finally(() => {
        setIsCancellingOrder(false);
        refreshCustomer();
      });
  };

  const changeOrderStatusToComplete = (timestamps: TimestampsMap) => {
    setIsMarkingComplete(true);

    const shipmentTimestamps = Object.entries(timestamps).map(([shipmentId, ts]) => ({
      shipment_id: shipmentId,
      shipped: ts.shipped?.toISOString() ?? null,
      in_transit: ts.in_transit?.toISOString() ?? null,
      delivered: ts.delivered?.toISOString() ?? null,
    }));

    axios
      .put(`orders/${order.id}/mark_order_complete`, {
        shipment_timestamps: shipmentTimestamps,
      })
      .then(() => {
        setShowMarkCompleteModal(false);
        toast.success('Order status updated to complete.', {
          ...BOTTOM_TOAST,
          autoClose: TWO_MINUTE_LONG_TOAST,
        });
        setTimeout(() => window.location.reload(), 3000);
      })
      .catch((e) => {
        toast.error(generateErrorMsg(e), {
          ...BOTTOM_TOAST,
          autoClose: TWO_MINUTE_LONG_TOAST,
        });
      })
      .finally(() => {
        setIsMarkingComplete(false);
      });
  };

  return (
    <div className="pb-3">
      <h2 className="mb-1 font-bold">Order Details</h2>
      <div className="bg-white border rounded-lg p-4 shadow-md w-full min-h-[262px]">
        <div className="mb-2 flex">
          <strong className="mr-1">Order Status: </strong> {order.status}
          {order.status === 'FULFILLING' && (
            <span
              className={`ml-2${orderContainsGoBoltFulfillment ? ' cursor-not-allowed' : ''}`}
              data-tooltip-id={orderContainsGoBoltFulfillment ? 'gobolt-cancel-tooltip' : undefined}
            >
              <Button
                className={`px-[2px] py-[2px]${
                  orderContainsGoBoltFulfillment ? ' pointer-events-none' : ''
                }`}
                variant="secondary"
                textClassName="text-sm font-normal"
                label="Mark as cancelled"
                onClick={() => setShowCancelConfirmModal(true)}
                isDisabled={orderContainsGoBoltFulfillment}
              />
              <Tooltip id="gobolt-cancel-tooltip" place="top" positionStrategy="fixed">
                This order is ineligible for cancellation because it is being fulfilled by GoBolt
                Canadian Fulfillment Center
              </Tooltip>
            </span>
          )}
          {order.status === OrderStatus.FULFILLED && canMarkComplete && (
            <span className="ml-2">
              <Button
                className="px-[2px] py-[2px]"
                variant="secondary"
                textClassName="text-sm font-normal"
                label="Mark as complete"
                onClick={() => setShowMarkCompleteModal(true)}
              />
            </span>
          )}
        </div>
        <div className="mb-2">
          <strong>Order Date:</strong> {mapSntApiDate(orderDate)}
        </div>
        <div className="mb-2">
          <strong>Order Type:</strong> {order.order_type}
        </div>
        <div className="mb-2">
          <strong>Order Total:</strong> ${order.total_price} {order.currency}
        </div>
        <div className="mb-2 flex items-center">
          <strong className="mr-1">Discount Code:</strong>
          {order.discounts.length === 0 ? (
            order.status === OrderStatus.PREVIEW && showDiscountActions ? (
              <Button
                className="px-[2px] py-[2px] ml-2"
                variant="secondary"
                textClassName="text-sm font-normal"
                label="Apply Discount"
                onClick={() => setShowAdhocDiscountModal(true)}
              />
            ) : (
              '--'
            )
          ) : (
            <>
              {order.discounts[0]?.discount?.code}
              {order.status === OrderStatus.PREVIEW && (
                <TrashIcon
                  className="inline w-4 h-4 mx-1 cursor-pointer"
                  onClick={() => setShowDeleteDiscountModal(true)}
                />
              )}
            </>
          )}
        </div>
        <div className="mb-2">
          <strong>Pets:</strong> {petNames.length > 0 ? petNamesStr : '--'}
        </div>
        <div className="mb-2">
          <strong>Pricing Model:</strong> {order.pricing_model?.name}
        </div>
      </div>
      {showCancelConfirmModal && (
        <ConfirmationModal
          title="Are you sure you want to proceed?"
          message={`By clicking "Confirm" below,
            you will change order status to cancelled`}
          confirmLabel="Confirm"
          isProcessLoading={isCancellingOrder}
          onConfirm={changeOrderStatusToCancelled}
          onCancel={() => setShowCancelConfirmModal(false)}
        />
      )}
      {showRefundConfirmModal && (
        <ConfirmationModal
          title="Do you want to refund the order?"
          message={`By clicking "Confirm" below,
            you will be taken to the refunds page`}
          confirmLabel="Confirm"
          onConfirm={() => {
            setShowRefundConfirmModal(false);
            window.location.href = `${window.location.href}/refund`;
          }}
          onCancel={() => setShowRefundConfirmModal(false)}
        />
      )}
      {showPostCancelModal && (
        <PostCancelModal
          onConfirm={() => console.log('Send to a different address')} // TODO:(SWE-6187)
          isDisabled={!isEligibleForModificationOrder}
          isSecondaryActionDisabled={
            !isRefundEligible || !hasRefundableBalance || isLoadingDisputeStatus
          }
          onSecondaryAction={() => {
            setShowPostCancelModal(false);
            window.location.href = `${window.location.href}/refund`;
          }}
          onCancel={() => setShowPostCancelModal(false)}
        />
      )}
      {showAdhocDiscountModal && (
        <AdhocDiscountModal
          customer={customer}
          order={order}
          refreshCustomer={refreshCustomer}
          onConfirm={() => {
            setShowAdhocDiscountModal(false);
          }}
          onCancel={() => setShowAdhocDiscountModal(false)}
        />
      )}
      {showDeleteDiscountModal && (
        <DiscountDeletionModal
          order={order}
          refreshCustomer={refreshCustomer}
          onConfirm={() => {
            setShowDeleteDiscountModal(false);
          }}
          onCancel={() => setShowDeleteDiscountModal(false)}
        />
      )}
      {showMarkCompleteModal && (
        <MarkCompleteModal
          order={order}
          onConfirm={changeOrderStatusToComplete}
          onCancel={() => setShowMarkCompleteModal(false)}
          isLoading={isMarkingComplete}
        />
      )}
    </div>
  );
};
